import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';

function Table({ headers = [], minCellWidth, tableContent }) {
  const [colSelectedForResizeIdx, setColSelectedForResizeIdx] = useState(null);
  const tableWrapperRef = useRef(null);
  const tableElement = useRef(null);
  const columnRefs = useRef(headers.map(() => React.createRef()));

  useEffect(() => {
    columnRefs.current = headers.map(() => React.createRef());
  }, [headers]);

  const handleColumnResize = useCallback(
    (e) => {
      if (
        colSelectedForResizeIdx === null ||
        !columnRefs.current[colSelectedForResizeIdx]
      )
        return;

      const gridColumns = headers.map((_, i) => {
        const columnRef = columnRefs.current[i]?.current;
        if (i === colSelectedForResizeIdx && columnRef) {
          const width = e.clientX - columnRef.getBoundingClientRect().left;
          if (width >= minCellWidth) {
            return `${width}px`;
          }
        }
        return columnRef ? `${columnRef.offsetWidth}px` : 'auto';
      });

      tableElement.current.style.gridTemplateColumns = `${gridColumns.join(' ')}`;
    },
    [colSelectedForResizeIdx, headers, minCellWidth],
  );

  const cleanupResizeListeners = useCallback(() => {
    window.removeEventListener('mousemove', handleColumnResize);
    window.removeEventListener('mouseup', endColumnResize);
  }, [handleColumnResize]);

  const endColumnResize = useCallback(() => {
    setColSelectedForResizeIdx(null);
    cleanupResizeListeners();
  }, [cleanupResizeListeners]);

  useEffect(() => {
    if (colSelectedForResizeIdx !== null) {
      window.addEventListener('mousemove', handleColumnResize);
      window.addEventListener('mouseup', endColumnResize);
    }

    return () => {
      cleanupResizeListeners();
    };
  }, [
    colSelectedForResizeIdx,
    handleColumnResize,
    endColumnResize,
    cleanupResizeListeners,
  ]);

  return (
    <Container fluid className="m-0 px-0">
      <div
        className="table-wrapper"
        ref={tableWrapperRef}
        style={{
          height: 'calc(100vh - 169px)',
          overflowY: 'auto',
          overflowX: 'auto',
          position: 'relative',
          width: '100%',
        }}
      >
        <table
          className="custom-vehicle-tracking-table"
          ref={tableElement}
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            display: 'grid',
            gridTemplateColumns: `repeat(${headers.length}, minmax(120px, auto))`,
          }}
        >
          <thead
            className="table-head-container"
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              background: '#fff',
            }}
          >
            <tr style={{ display: 'contents' }}>
              {headers.map((headerObj, idx) => (
                <th
                  className="table-header"
                  ref={columnRefs.current[idx]}
                  key={`header-${headerObj.label}`}
                  style={{
                    borderBottom: '1px solid #dbdbdb',
                    background: '#fff',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    padding: '8px 15px',
                  }}
                >
                  <div className="div-flex-row cg-10 div-align-center">
                    <span className="text-sm text-semi-bold text-grey">
                      {headerObj.label}
                    </span>
                  </div>
                  <button
                    style={{ height: '60%', top: '20%' }}
                    onMouseDown={() => setColSelectedForResizeIdx(idx)}
                    className={`resize-handle ${
                      colSelectedForResizeIdx === idx ? 'active' : 'idle'
                    }`}
                    aria-label={`Resize column ${headerObj.label}`}
                  />
                </th>
              ))}
            </tr>
          </thead>

          <tbody style={{ display: 'contents' }}>{tableContent}</tbody>
        </table>
      </div>
    </Container>
  );
}

export default Table;
